import { createContext, useContext, ReactNode } from "react";
import { useSocket } from "@/hooks/useSockets";
import { useAuthStore } from "@/store/useAuthStore";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";

interface SocketContextType {
    connected: boolean;
    sendMessage: (text: string, chatId: string) => void;
    sendTyping: (chatId: string) => void;
}

const SocketContext = createContext<SocketContextType | null>(null);

export function SocketProvider({ children }: { children: ReactNode }) {
    const token = useAuthStore((state) => state.token);
    const { hasPermission } = usePermissions();
    const hasActiveChatPermission = hasPermission(PERMISSIONS.ACTIVE_CHAT);

    // Only initialize socket if user has ACTIVE_CHAT permission
    const { connected, sendMessage: originalSendMessage, sendTyping: originalSendTyping } = useSocket(
        undefined,
        hasActiveChatPermission ? (token || undefined) : undefined
    );

    // Wrapper to make sendMessage accept chatId as parameter
    const sendMessage = (text: string, chatId: string) => {
        originalSendMessage(text, chatId);
    };

    // Return null context if user doesn't have permission
    if (!hasActiveChatPermission) {
        return <>{children}</>;
    }

    return (
        <SocketContext.Provider value={{ connected, sendMessage, sendTyping: originalSendTyping }}>
            {children}
        </SocketContext.Provider>
    );
}

export function useSocketContext() {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error("useSocketContext must be used within SocketProvider");
    }
    return context;
}
